import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { SlidersHorizontal, X } from "lucide-react";
import {
  getSubcategoriesFor,
  productMainCategories,
} from "../../utils/productCategories";

const Field = ({ label, children }) => (
  <label className="block space-y-1.5">
    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
      {label}
    </span>
    {children}
  </label>
);

const selectClass =
  "w-full h-10 px-3 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C]";

/**
 * Right-side filter drawer for inventory list.
 */
const InventoryFilterDrawer = ({
  isOpen,
  filters,
  options,
  onClose,
  onApply,
  onClear,
}) => {
  const [draft, setDraft] = useState(filters);

  useEffect(() => {
    if (!isOpen) return;
    setDraft(filters);
  }, [isOpen, filters]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prev;
    };
  }, [isOpen, onClose]);

  const subcategoryOptions = useMemo(() => {
    if (!draft.category) return [];
    return getSubcategoriesFor(draft.category);
  }, [draft.category]);

  const update = (key, value) => {
    setDraft((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "category") {
        const allowed = value ? getSubcategoriesFor(value) : [];
        if (next.subcategory && !allowed.includes(next.subcategory)) {
          next.subcategory = "";
        }
      }
      return next;
    });
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isOpen ? (
        <>
          <motion.div
            key="inventory-filter-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/40"
            onClick={onClose}
          />
          <motion.aside
            key="inventory-filter-panel"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 280 }}
            className="fixed inset-y-0 right-0 z-[70] flex w-full max-w-md flex-col bg-white shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="inventory-filter-title"
          >
            <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="h-4 w-4 text-[#FF6C6C]" />
                <h2
                  id="inventory-filter-title"
                  className="text-base font-semibold text-gray-900"
                >
                  Filters
                </h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close filters"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
              <Field label="Category">
                <select
                  value={draft.category}
                  onChange={(e) => update("category", e.target.value)}
                  className={selectClass}
                >
                  <option value="">All categories</option>
                  {productMainCategories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Subcategory">
                <select
                  value={draft.subcategory}
                  onChange={(e) => update("subcategory", e.target.value)}
                  className={selectClass}
                  disabled={!draft.category}
                >
                  <option value="">
                    {draft.category
                      ? "All subcategories"
                      : "Select a category first"}
                  </option>
                  {subcategoryOptions.map((sub) => (
                    <option key={sub} value={sub}>
                      {sub}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Stock status">
                <select
                  value={draft.stock}
                  onChange={(e) => update("stock", e.target.value)}
                  className={selectClass}
                >
                  <option value="all">All stock</option>
                  <option value="in_stock">In stock</option>
                  <option value="low">Low stock (≤5)</option>
                  <option value="out">Out of stock</option>
                </select>
              </Field>

              <Field label="Deal status">
                <select
                  value={draft.status}
                  onChange={(e) => update("status", e.target.value)}
                  className={selectClass}
                >
                  <option value="">All statuses</option>
                  {(options.statuses || []).map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Variants">
                <select
                  value={draft.variants}
                  onChange={(e) => update("variants", e.target.value)}
                  className={selectClass}
                >
                  <option value="all">All products</option>
                  <option value="with">With size variants</option>
                  <option value="without">No size variants</option>
                </select>
              </Field>

              <Field label="Offer type">
                <select
                  value={draft.offerType}
                  onChange={(e) => update("offerType", e.target.value)}
                  className={selectClass}
                >
                  <option value="">All offer types</option>
                  {(options.offerTypes || []).map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="flex items-center justify-between gap-2 border-t border-gray-200 px-5 py-4">
              <button
                type="button"
                onClick={() => {
                  onClear?.();
                  onClose?.();
                }}
                className="h-9 rounded-md px-3 text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                Clear all
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="h-9 rounded-md border border-gray-200 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onApply?.(draft);
                    onClose?.();
                  }}
                  className="h-9 rounded-md bg-[#FF6C6C] px-4 text-sm font-semibold text-white hover:bg-[#e85c5c]"
                >
                  Apply filters
                </button>
              </div>
            </div>
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>,
    document.body
  );
};

export default InventoryFilterDrawer;
